import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { staffApi, errMsg } from "@/lib/api";
import { CATEGORIES, timeLabel } from "@/components/staff/categories";
import { useContent } from "@/lib/content-store";
import { occursOn } from "@/lib/schedule";
import { EventDialog } from "@/components/staff/EventDialog";

const Legend = ({ filter, setFilter }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(CATEGORIES).map(([k, c]) => {
      const on = filter.includes(k);
      return (
        <button key={k} data-testid={`calendar-filter-${k}`}
          onClick={() => setFilter(on ? filter.filter((f) => f !== k) : [...filter, k])}
          className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-opacity ${on ? "opacity-100" : "opacity-40"}`}
          style={{ borderColor: c.color, color: c.color, background: c.bg }}>
          <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.label}
        </button>
      );
    })}
  </div>
);

const DayCell = ({ day, month, events, onAdd, onOpen }) => (
  <div className={`group min-h-[60px] border-b border-r border-slate-100 p-1 sm:min-h-[112px] sm:p-1.5 ${isSameMonth(day, month) ? "bg-white" : "bg-slate-50/60"}`}
    data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}>
    <div className="flex items-center justify-between">
      <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold sm:h-7 sm:w-7 sm:text-xs ${isToday(day) ? "bg-[#0A3A6B] text-white" : isSameMonth(day, month) ? "text-slate-700" : "text-slate-300"}`}>
        {format(day, "d")}
      </span>
      <button onClick={() => onAdd(day)} className="hidden rounded p-1 text-slate-300 opacity-0 transition-opacity hover:text-[#1D64B4] group-hover:opacity-100 sm:block" data-testid={`calendar-add-${format(day, "yyyy-MM-dd")}`}>
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
    <button onClick={() => (events[0] ? onOpen(events[0]) : onAdd(day))} className="mt-1 flex w-full flex-wrap gap-0.5 sm:hidden" data-testid={`calendar-mobile-day-${format(day, "yyyy-MM-dd")}`}>
      {events.length === 0 && <span className="h-4 w-full" />}
      {events.slice(0, 4).map((e) => <span key={e.id} className="h-1.5 w-1.5 rounded-full" style={{ background: CATEGORIES[e.category].color }} />)}
    </button>
    <div className="mt-1 hidden space-y-1 sm:block">
      {[...events].sort((a, b) => (a.category === "worship") - (b.category === "worship")).slice(0, 3).map((e) => {
        const c = CATEGORIES[e.category];
        return (
          <button key={e.id} onClick={() => onOpen(e)} data-testid={`calendar-event-${e.id}`}
            className="block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium transition-transform hover:scale-[1.02]"
            style={{ background: c.bg, color: c.color, borderLeft: `3px solid ${c.color}` }}>
            {e.start_time && `${timeLabel(e.start_time)} `}{e.title}
          </button>
        );
      })}
      {events.length > 3 && <p className="px-1 text-[11px] text-slate-400">+{events.length - 3} more</p>}
    </div>
  </div>
);

const Agenda = ({ events, staff, onOpen }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-6">
    <h3 className="text-lg font-semibold text-slate-900">This month</h3>
    <div className="mt-4 space-y-3" data-testid="calendar-agenda">
      {events.length === 0 && <p className="text-sm text-slate-400">No schedules yet. Click a day to add one.</p>}
      {events.map((e) => {
        const c = CATEGORIES[e.category];
        const who = staff.find((s) => s.id === e.assigned_to);
        return (
          <button key={e.id} onClick={() => onOpen(e)} className="flex w-full gap-3 rounded-xl p-2 text-left hover:bg-slate-50" data-testid={`agenda-item-${e.id}`}>
            <div className="w-12 shrink-0 text-center"><p className="text-[10px] font-semibold uppercase text-slate-400">{format(parseISO(e.date), "EEE")}</p><p className="font-display text-xl font-bold text-slate-900">{format(parseISO(e.date), "d")}</p></div>
            <div className="min-w-0 flex-1 border-l-2 pl-3" style={{ borderColor: c.color }}>
              <p className="truncate font-medium text-slate-900">{e.title}</p>
              <p className="text-xs text-slate-500">{c.label}{e.start_time && ` · ${timeLabel(e.start_time)}`}{who && ` · ${who.name}`}{e.minister_name && ` · Minister: ${e.minister_name}`}</p>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

export const StaffCalendar = ({ role }) => {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState(Object.keys(CATEGORIES));
  const [dialog, setDialog] = useState({ open: false, event: null, date: null });

  const load = useCallback(() => {
    staffApi().get("/events", { params: { month: format(month, "yyyy-MM") } }).then((r) => setEvents(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { staffApi().get("/staff-members").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(month), end: endOfWeek(endOfMonth(month)) }), [month]);
  const { schedules = [] } = useContent();
  const recurring = useMemo(() => days.filter((d) => isSameMonth(d, month)).flatMap((d) => schedules.filter((s) => occursOn(s, d)).map((s) => ({
    id: `w-${s.id}-${format(d, "yyyyMMdd")}`, title: `${s.title}${s.note ? ` (${s.note})` : ""}`, category: "worship",
    date: format(d, "yyyy-MM-dd"), start_time: s.time, readonly: true,
  }))), [days, month, schedules]);
  const visible = [...events, ...recurring].filter((e) => filter.includes(e.category)).sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const openEvent = (e) => (e.readonly ? toast.info("Worship schedules are edited in the Pastoral & Schedules tab (admin).") : role !== "admin" ? toast.info(`${e.title} · events are managed by admins`) : setDialog({ open: true, event: e, date: null }));

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_320px]" data-testid="staff-calendar">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="min-w-[150px] text-center text-xl font-bold sm:min-w-[180px] sm:text-2xl tracking-tight text-slate-900" data-testid="calendar-month-label">{format(month, "MMMM yyyy")}</h2>
            <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-month"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setMonth(startOfMonth(new Date()))} data-testid="calendar-today">Today</Button>
          </div>
          {role === "admin" ? <Button onClick={() => setDialog({ open: true, event: null, date: new Date() })} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="calendar-new-event-button">
            <Plus className="mr-1 h-4 w-4" /> New schedule
          </Button> : <span className="text-xs text-slate-500" data-testid="calendar-view-only-note">View only · events are set up by admins</span>}
        </div>
        <Legend filter={filter} setFilter={setFilter} />
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white sm:rounded-3xl">
          <div className="grid grid-cols-7">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="border-b border-r border-slate-100 bg-slate-50 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500 sm:text-xs">{d}</div>
            ))}
            {days.map((day) => (
              <DayCell key={day.toISOString()} day={day} month={month}
                events={visible.filter((e) => e.date === format(day, "yyyy-MM-dd"))}
                onAdd={(d) => role === "admin" && setDialog({ open: true, event: null, date: d })}
                onOpen={openEvent} />
            ))}
          </div>
        </div>
      </div>
      <Agenda events={visible.filter((e) => !e.readonly)} staff={staff} onOpen={openEvent} />
      <EventDialog state={dialog} staff={staff} onClose={() => setDialog({ open: false, event: null, date: null })} onSaved={load} />
    </div>
  );
};
